import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import StarIcon from '@material-ui/icons/Star';
import CloseIcon from '@material-ui/icons/Close';

import { LeaderboardScore } from 'api';
import { UserAvatar } from 'components';
import { getFullName } from 'helpers';

import PointTable from './PointTable';

const styles = (theme: Theme) =>
  createStyles({
    title: {
      marginBottom: 0,
      paddingBottom: theme.spacing(2),
    },
    actions: {
      margin: theme.spacing(3),
    },
  });

interface IProps extends WithStyles<typeof styles> {
  open: boolean;
  onClose: () => void;
  leaderboardScore: LeaderboardScore;
  renderRank: (ranking: LeaderboardScore['ranking']) => ReactNode;
  renderChange: (change: LeaderboardScore['position_change']) => ReactNode;
}

const UserPointDialog: FC<IProps> = props => {
  const { t } = useTranslation();
  const { classes, open, onClose, renderRank, renderChange, leaderboardScore } = props;
  const { events, position_change, ranking, score, user } = leaderboardScore;

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle disableTypography className={classes.title}>
        <Grid container alignItems="center">
          <Grid item xs>
            <Grid container alignItems="center" spacing={3}>
              <Grid item>{renderRank(ranking)}</Grid>
              <Grid item>
                <Grid container alignItems="center">
                  {renderChange(position_change)}
                </Grid>
              </Grid>
              <Grid item>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <UserAvatar link size={50} user={user} />
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2">{getFullName(user)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <PointTable eventScores={events} />
      <DialogActions className={classes.actions}>
        <Grid container justify="flex-end" alignItems="center" spacing={1}>
          <Grid item>
            <Typography variant="subtitle2">{t('leaderboard:totalScore', { score })}</Typography>
          </Grid>
          <Grid item>
            <StarIcon color="primary" />
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(UserPointDialog);
